import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { Page, PageHeader } from "@/components/PageHeader";
import { Funnel, Plus, UsersThree, Trash } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";

const FIELDS = [
  { v: "lifetime_value", l: "Lifetime value" },
  { v: "total_stays", l: "Total stays" },
  { v: "last_activity_days", l: "Days since activity" },
  { v: "sentiment", l: "Sentiment" },
  { v: "loyalty_tier", l: "Loyalty tier" },
];
const OPS = [{ v: ">", l: "greater than" }, { v: ">=", l: "at least" }, { v: "<", l: "less than" }, { v: "=", l: "equals" }, { v: "contains", l: "contains" }];

export default function Segments() {
  const qc = useQueryClient();
  const { data: segments = [] } = useQuery({ queryKey: ["segments"], queryFn: async () => (await api.get("/segments")).data });
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [rule, setRule] = useState({ field: "lifetime_value", op: ">", value: "1000" });

  const create = useMutation({
    mutationFn: async () => {
      const value = ["sentiment", "loyalty_tier"].includes(rule.field) ? rule.value : Number(rule.value);
      return (await api.post("/segments", { name, description: desc, rules: [{ ...rule, value }] })).data;
    },
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ["segments"] });
      toast.success(`Segment "${d.name}" created — ${d.guest_count} guests matched`);
      setOpen(false); setName(""); setDesc("");
    },
    onError: () => toast.error("Could not create segment"),
  });

  return (
    <Page>
      <PageHeader title="Segments" subtitle="Dynamic audiences from guest attributes & behavior.">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-segment-btn" className="gap-1.5"><Plus size={16} weight="bold" /> New segment</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Create segment</DialogTitle></DialogHeader>
            <div className="space-y-3.5">
              <div>
                <label className="text-[13px] font-medium mb-1.5 block">Name</label>
                <input data-testid="segment-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Summer VIPs" className="w-full h-10 px-3 rounded-md border border-border text-sm outline-none focus:ring-2 focus:ring-primary/30" />
              </div>
              <div>
                <label className="text-[13px] font-medium mb-1.5 block">Description</label>
                <input value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Optional" className="w-full h-10 px-3 rounded-md border border-border text-sm outline-none focus:ring-2 focus:ring-primary/30" />
              </div>
              <div>
                <label className="text-[13px] font-medium mb-1.5 block">Rule</label>
                <div className="flex gap-2">
                  <Select value={rule.field} onValueChange={(v) => setRule({ ...rule, field: v })}>
                    <SelectTrigger className="flex-1" data-testid="rule-field"><SelectValue /></SelectTrigger>
                    <SelectContent>{FIELDS.map((f) => <SelectItem key={f.v} value={f.v}>{f.l}</SelectItem>)}</SelectContent>
                  </Select>
                  <Select value={rule.op} onValueChange={(v) => setRule({ ...rule, op: v })}>
                    <SelectTrigger className="w-[130px]" data-testid="rule-op"><SelectValue /></SelectTrigger>
                    <SelectContent>{OPS.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
                  </Select>
                  <input data-testid="rule-value" value={rule.value} onChange={(e) => setRule({ ...rule, value: e.target.value })} className="w-[110px] h-10 px-3 rounded-md border border-border text-sm outline-none focus:ring-2 focus:ring-primary/30" />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button data-testid="save-segment-btn" disabled={!name || create.isPending} onClick={() => create.mutate()}>{create.isPending ? "Computing…" : "Create segment"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </PageHeader>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {segments.map((s, i) => (
          <div key={s.id} data-testid={`segment-card-${i}`} className="fade-up rounded-lg border border-border bg-card p-5 hover:shadow-md transition-shadow" style={{ animationDelay: `${i * 50}ms` }}>
            <div className="flex items-start justify-between">
              <div className="h-9 w-9 rounded-md bg-primary/10 text-primary flex items-center justify-center"><Funnel size={18} weight="fill" /></div>
              <span className="flex items-center gap-1.5 text-sm font-medium text-primary"><UsersThree size={16} weight="fill" />{s.guest_count}</span>
            </div>
            <h3 className="font-heading font-semibold mt-3.5">{s.name}</h3>
            <p className="text-sm text-muted-foreground mt-1 min-h-[40px]">{s.description}</p>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {s.rules.map((r, j) => (
                <span key={j} className="text-[11px] px-2 py-0.5 rounded bg-secondary border border-border font-mono">{r.field} {r.op} {String(r.value)}</span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </Page>
  );
}
